/**
 * Auto-generate JSON-LD structured data from page data and layout blocks.
 */

import regions from '@/lib/geo';
import { breadcrumbTrail } from '@/lib/breadcrumbs';

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL || 'https://www.officeportal.io';

/**
 * Serialise a JSON-LD graph for `<script type="application/ld+json">`.
 * `<` is emitted as `\u003c` (still valid JSON, parses back to `<`), so a
 * value containing `</script>` or `<!--` cannot terminate the script element
 * and inject markup. Use this — never raw JSON.stringify — for inline JSON-LD.
 */
export function jsonLdHtml(graph: unknown): string {
  return JSON.stringify(graph).replace(/</g, '\\u003c');
}

interface JsonLdGraph {
  '@context': 'https://schema.org';
  '@graph': Record<string, unknown>[];
}

/** Build the base Organization + WebSite nodes */
function baseGraph(): Record<string, unknown>[] {
  return [
    {
      '@type': 'Organization',
      '@id': `${SITE_URL}/#organization`,
      name: 'OfficePortal',
      url: SITE_URL,
      logo: {
        '@type': 'ImageObject',
        // Dark wordmark: Google renders the Organization logo on a white
        // background, so the white variant (used in the dark header/footer)
        // would be invisible. (The old /images/logo.png 404'd.)
        url: `${SITE_URL}/OfficePortal-black.svg`,
      },
      description:
        'OfficePortal is an all-in-one HR management (HRMS) platform that helps organizations automate attendance, payroll, leave, performance, and employee management.',
      // Official profiles — must match the links the site footer renders
      // (SiteSettings → socialLinks). Update BOTH places if a profile changes.
      // x.com is the canonical Twitter/X domain since the rebrand. Review-site
      // profiles (G2, Capterra) belong here too — add them once the exact
      // profile URLs are confirmed; never guess a profile URL.
      sameAs: [
        'https://www.linkedin.com/products/officeportal-hrms/',
        'https://www.linkedin.com/showcase/officeportal',
        'https://www.facebook.com/OfficePortalHRMS',
        'https://x.com/officeportalhrm',
        'https://www.youtube.com/@officeportalhrms1969',
        'https://www.instagram.com/officeportal_hrms/',
      ],
      // OfficePortal sells internationally — only the payroll module is
      // India-specific. areaServed is the honest schema counterweight to the
      // Indian legal-entity facts below (parentOrganization address, +91 phone):
      // it says "serves a worldwide market" without faking a foreign presence.
      areaServed: 'Worldwide',
      // Both addresses verified live on the site (home + contact pages).
      contactPoint: [
        { '@type': 'ContactPoint', contactType: 'sales', email: 'sales@officeportal.io', telephone: '+91 84548 69085', availableLanguage: ['English'], areaServed: 'Worldwide' },
        { '@type': 'ContactPoint', contactType: 'customer support', email: 'support@officeportal.io', availableLanguage: ['English'], areaServed: 'Worldwide' },
      ],
      // OfficePortal is a product suite, not a separate business — the legal
      // entity (and registered address/phone) is Awarious Technology Solutions.
      parentOrganization: {
        '@type': 'Organization',
        name: 'Awarious Technology Solutions',
        url: 'https://www.awarious.com',
        telephone: '+91 84548 69085',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'F3, Plot 218, Atrium Spring Meadows, Ram Nagar 3rd St, Velachery',
          addressLocality: 'Chennai',
          addressRegion: 'Tamil Nadu',
          postalCode: '600042',
          addressCountry: 'IN',
        },
      },
    },
    {
      '@type': 'WebSite',
      '@id': `${SITE_URL}/#website`,
      url: SITE_URL,
      name: 'OfficePortal',
      publisher: { '@id': `${SITE_URL}/#organization` },
    },
  ];
}

/** Canonical URL for a page — the homepage is the site root, never /home. */
function pageUrl(slug: string): string {
  return !slug || slug === 'home' ? SITE_URL : `${SITE_URL}/${slug}`;
}

/**
 * Build BreadcrumbList for a page from the shared breadcrumb trail
 * (src/lib/breadcrumbs.ts) — the SAME source the visible <Breadcrumbs> uses, so
 * the on-page trail and this structured data can never disagree.
 * Returns null on the homepage (a "Home → Home" breadcrumb is noise).
 */
function breadcrumb(slug: string, title: string): Record<string, unknown> | null {
  const trail = breadcrumbTrail(slug, title, SITE_URL);
  if (!trail) return null;

  return {
    '@type': 'BreadcrumbList',
    itemListElement: trail.map((crumb, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: crumb.name,
      item: crumb.href,
    })),
  };
}

/** Build SoftwareApplication node for product pages.
 *
 * Reviews are nested INSIDE this node (with a computed aggregateRating) rather
 * than emitted as top-level Review objects: a Review without `itemReviewed` is
 * flagged as an error by Google's Rich Results Test and earns nothing. */
function softwareApp(page: any, layout: any[], reviews: Record<string, unknown>[]): Record<string, unknown> {
  // featureList from the page's own feature blocks — deduped, order preserved
  const featureList: string[] = [];
  for (const b of layout) {
    if (b.blockType !== 'featureGrid' && b.blockType !== 'featureShowcase') continue;
    for (const f of b.features || []) {
      if (f?.title && !featureList.includes(f.title)) featureList.push(f.title);
    }
  }

  const description = page.seo?.metaDescription || page.excerpt || '';
  const screenshot = page.seo?.ogImage?.url || page.heroImage?.url || '';

  const app: Record<string, unknown> = {
    '@type': 'SoftwareApplication',
    name: page.title || 'OfficePortal',
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: 'HR Software',
    operatingSystem: 'Web',
    url: `${SITE_URL}/${page.slug}`,
    // Ties every product node to the same Organization entity in the graph.
    publisher: { '@id': `${SITE_URL}/#organization` },
    // Platform-wide release notes page (48+ dated releases)
    releaseNotes: `${SITE_URL}/whats-new-in-officeportal`,
    ...(description ? { description } : {}),
    ...(screenshot ? { screenshot } : {}),
    ...(featureList.length ? { featureList } : {}),
    // Free → Elite span. USD/GLOBAL prices from lib/geo — the same source the
    // pricing UI renders at SSR time, so schema always matches what Googlebot
    // sees. (Geo-INR is a post-hydration client swap; schema stays USD.)
    offers: {
      '@type': 'AggregateOffer',
      lowPrice: '0',
      highPrice: String(regions.GLOBAL.pricing.elite),
      priceCurrency: regions.GLOBAL.currency,
      offerCount: 3, // Standard (free) / Premium / Elite
      description: 'Free plan available',
    },
  };

  if (reviews.length) {
    app.review = reviews;
    const rated = reviews.filter((r) => (r.reviewRating as any)?.ratingValue);
    if (rated.length) {
      const avg = rated.reduce((sum, r) => sum + Number((r.reviewRating as any).ratingValue), 0) / rated.length;
      app.aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: Math.round(avg * 10) / 10,
        reviewCount: rated.length,
        bestRating: 5,
      };
    }
  }

  return app;
}

/**
 * Depth-first plain text of a lexical tree. Inline children concatenate as-is
 * (formatting splits like bold produce adjacent text nodes that already carry
 * their own spacing); block-level containers (root, list) separate their
 * children with a space so paragraphs don't run together.
 */
function lexicalPlainText(node: any): string {
  if (!node || typeof node !== 'object') return '';
  if (typeof node.text === 'string') return node.text;
  if (!Array.isArray(node.children)) return '';
  const isBlockParent = node.type === 'root' || node.type === 'list';
  return node.children.map(lexicalPlainText).filter(Boolean).join(isBlockParent ? ' ' : '');
}

/** Extract FAQPage schema from an array of `faq` blocks (used by product-page
 *  layouts and by the blog post FAQ section). Returns null when there are none. */
export function faqSchema(blocks: any[]): Record<string, unknown> | null {
  const faqBlocks = blocks.filter((b: any) => b.blockType === 'faq');
  if (!faqBlocks.length) return null;

  const items = faqBlocks.flatMap((b: any) =>
    (b.items || []).map((item: any) => ({
      '@type': 'Question',
      name: item.question || '',
      acceptedAnswer: {
        '@type': 'Answer',
        // Full plain text of the whole answer — the previous extractor took only
        // the first text node of the first paragraph, truncating any answer with
        // inline formatting or multiple paragraphs.
        text: typeof item.answer === 'string' ? item.answer : lexicalPlainText(item.answer?.root).trim(),
      },
    }))
  );

  if (!items.length) return null;

  return {
    '@type': 'FAQPage',
    mainEntity: items,
  };
}

/** Extract HowTo schema from howItWorks blocks */
function howToSchema(blocks: any[]): Record<string, unknown> | null {
  const htBlocks = blocks.filter((b: any) => b.blockType === 'howItWorks');
  if (!htBlocks.length) return null;

  const block = htBlocks[0];
  const steps = (block.steps || []).map((step: any, i: number) => ({
    '@type': 'HowToStep',
    position: i + 1,
    name: step.title || '',
    text: step.description || '',
  }));

  if (!steps.length) return null;

  return {
    '@type': 'HowTo',
    name: block.heading || 'How it works',
    step: steps,
  };
}

/** Extract Review schema from testimonials blocks */
function reviewSchema(blocks: any[]): Record<string, unknown>[] {
  const tBlocks = blocks.filter((b: any) => b.blockType === 'testimonials');
  const reviews: Record<string, unknown>[] = [];

  for (const block of tBlocks) {
    for (const item of block.items || []) {
      if (!item.quote) continue;
      reviews.push({
        '@type': 'Review',
        reviewBody: item.quote,
        author: {
          '@type': 'Person',
          name: item.authorName || 'Anonymous',
        },
        ...(item.rating ? { reviewRating: { '@type': 'Rating', ratingValue: item.rating, bestRating: 5 } } : {}),
      });
    }
  }

  return reviews;
}

/* Default product-tour video shown by the hero when no videoUrl is set.
 * KEEP IN SYNC with the fallback in blocks/renderers/HeroBlock.tsx. */
const DEFAULT_TOUR_VIDEO = 'https://www.youtube-nocookie.com/embed/5Ocf4Q-ocBk';

/** Extract the YouTube video id from an embed / watch / short url. */
function youtubeId(url: string): string | null {
  const m = url.match(/(?:embed\/|watch\?v=|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  return m ? m[1] : null;
}

/**
 * VideoObject nodes for hero product-tour videos. Tells engines the video
 * exists (name, thumbnail, embed) — YouTube embeds alone carry no metadata for
 * the host page. uploadDate uses the page's publish date when known and is
 * omitted otherwise, never fabricated.
 */
function videoSchema(blocks: any[], page: any): Record<string, unknown>[] {
  const out: Record<string, unknown>[] = [];
  for (const b of blocks) {
    if (b.blockType !== 'hero') continue;
    const url = b.videoUrl || (b.variant === 'typewriter' ? DEFAULT_TOUR_VIDEO : '');
    if (!url) continue;
    const id = youtubeId(url);
    if (!id) continue;
    const uploadDate = page.publishedAt || page.createdAt;
    out.push({
      '@type': 'VideoObject',
      name: b.videoLabel || 'OfficePortal product tour',
      description: 'A video walkthrough of the OfficePortal HRMS platform.',
      embedUrl: `https://www.youtube-nocookie.com/embed/${id}`,
      thumbnailUrl: `https://i.ytimg.com/vi/${id}/hqdefault.jpg`,
      ...(uploadDate ? { uploadDate } : {}),
    });
  }
  return out;
}

/**
 * Extract Product + Offer schema from pricingPreview blocks.
 *
 * The CMS `price` field is free text ("₹149", "$5", "Custom"), rendered as-is
 * on the page — so the schema derives from the same text: numeric value parsed
 * out, currency from the symbol (₹ → INR, default USD), and plans without a
 * numeric price ("Custom") emit no Offer at all rather than an invalid one.
 * The billing period becomes a proper UnitPriceSpecification ("/user/month" →
 * "per user per month") — the schema-valid home for what billingIncrement
 * (not a schema.org property) used to misstate.
 */
function pricingSchema(blocks: any[]): Record<string, unknown>[] {
  const pBlocks = blocks.filter((b: any) => b.blockType === 'pricingPreview');
  const products: Record<string, unknown>[] = [];

  for (const block of pBlocks) {
    for (const plan of block.plans || []) {
      if (!plan.name) continue;

      const raw = String(plan.price ?? '');
      const numeric = raw.match(/[\d,]+(?:\.\d+)?/)?.[0]?.replace(/,/g, '');
      if (!numeric) continue; // "Custom" etc. — no valid Offer possible

      const priceCurrency = raw.includes('₹') ? 'INR' : 'USD';
      const unitText = plan.period
        ? `per ${String(plan.period).replace(/^\/+/, '').split('/').filter(Boolean).join(' per ')}`
        : null;

      products.push({
        '@type': 'Product',
        name: plan.name,
        // description may be a lexical rich-text object — flatten to plain text
        description:
          typeof plan.description === 'string'
            ? plan.description
            : lexicalPlainText(plan.description?.root).trim(),
        offers: {
          '@type': 'Offer',
          price: numeric,
          priceCurrency,
          ...(unitText
            ? {
                priceSpecification: {
                  '@type': 'UnitPriceSpecification',
                  price: numeric,
                  priceCurrency,
                  unitText,
                },
              }
            : {}),
        },
      });
    }
  }

  return products;
}

/**
 * JSON-LD graph for a help-center article: shared Organization/WebSite nodes,
 * a 4-level breadcrumb (Home → Help Center → category → article, using the
 * category's real name, not a title-cased slug), and a TechArticle. Until now
 * the entire help section (150+ indexed pages) emitted no schema at all.
 */
export function generateHelpArticleJsonLd(doc: any): JsonLdGraph {
  const category = typeof doc.category === 'object' && doc.category ? doc.category : null;
  const catSlug = category?.slug || '';
  const url = `${SITE_URL}/help/${catSlug}/${doc.slug}`;

  const crumbs = [
    { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
    { '@type': 'ListItem', position: 2, name: 'Help Center', item: `${SITE_URL}/help` },
    ...(category
      ? [{ '@type': 'ListItem', position: 3, name: category.name, item: `${SITE_URL}/help/${catSlug}` }]
      : []),
    { '@type': 'ListItem', position: category ? 4 : 3, name: doc.title || '', item: url },
  ];

  const description = doc.seo?.metaDescription || doc.excerpt || '';

  return {
    '@context': 'https://schema.org',
    '@graph': [
      ...baseGraph(),
      { '@type': 'BreadcrumbList', itemListElement: crumbs },
      {
        '@type': 'TechArticle',
        headline: doc.title || '',
        url,
        mainEntityOfPage: url,
        ...(description ? { description } : {}),
        ...(doc.createdAt ? { datePublished: doc.createdAt } : {}),
        ...(doc.updatedAt ? { dateModified: doc.updatedAt } : {}),
        author: { '@id': `${SITE_URL}/#organization` },
        publisher: { '@id': `${SITE_URL}/#organization` },
      },
      // Structured FAQs (30 Jul): machine-readable Q&A pairs are the most
      // liftable format for AI assistants and answer engines. Google rarely
      // shows FAQ rich results since 2023 — that is NOT why this exists.
      // Junk pairs (blank question or answer) are filtered, matching the
      // page render, so schema never claims more than the page shows.
      ...(() => {
        const faqs = (Array.isArray(doc.faqs) ? doc.faqs : []).filter(
          (f: { question?: unknown; answer?: unknown }) =>
            typeof f?.question === 'string' && f.question.trim().length > 0 &&
            typeof f?.answer === 'string' && f.answer.trim().length > 0,
        );
        if (!faqs.length) return [];
        return [{
          '@type': 'FAQPage',
          '@id': `${url}#faq`,
          mainEntity: faqs.map((f: { question: string; answer: string }) => ({
            '@type': 'Question',
            name: f.question.trim(),
            acceptedAnswer: { '@type': 'Answer', text: f.answer.trim() },
          })),
        }];
      })(),
    ],
  };
}

export interface JsonLdOptions {
  /** For index/listing pages: emits an ItemList node and upgrades the WebPage
   * node to CollectionPage — tells engines the page is a catalog of the items. */
  itemList?: { name: string; items: { name: string; url: string }[] };
}

/**
 * Generate a full JSON-LD graph for a given page and its layout blocks.
 */
export function generateJsonLd(page: any, options: JsonLdOptions = {}): JsonLdGraph {
  const layout: any[] = Array.isArray(page.layout) ? page.layout : [];
  const graph: Record<string, unknown>[] = [...baseGraph()];

  const crumbs = breadcrumb(page.slug || '', page.title || '');
  if (crumbs) graph.push(crumbs);

  // Product pages get SoftwareApplication with testimonial reviews nested
  // inside it (+ aggregateRating). On non-product pages there is no valid
  // itemReviewed to attach them to, so testimonials stay visual-only — a
  // floating top-level Review is invalid markup that earns nothing.
  //
  // Guarded twice, because pageType alone proved unreliable: it DEFAULTED to
  // 'product', so careers/contact-us/pricing/whats-new all carried it and
  // emitted SoftwareApplication nodes SEMrush flagged as invalid ("a value for
  // the aggregateRating or review field is required"). Real products live
  // under products/*, and the node is only valid when reviews exist.
  const reviews = reviewSchema(layout);
  const isRealProduct = page.pageType === 'product' && String(page.slug || '').startsWith('products/');
  if (isRealProduct && reviews.length) {
    graph.push(softwareApp(page, layout, reviews));
  }

  // FAQ
  const faq = faqSchema(layout);
  if (faq) graph.push(faq);

  // HowTo
  const howTo = howToSchema(layout);
  if (howTo) graph.push(howTo);

  // Hero tour videos
  graph.push(...videoSchema(layout, page));

  // Pricing/Products
  const products = pricingSchema(layout);
  graph.push(...products);

  // Index pages: ItemList of the collection's entries (products etc.)
  if (options.itemList?.items.length) {
    graph.push({
      '@type': 'ItemList',
      name: options.itemList.name,
      itemListElement: options.itemList.items.map((item, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: item.name,
        url: item.url,
      })),
    });
  }

  // WebPage node — the homepage identifies as the site root, not /home
  // (the slug-based URL fed the /home duplicate-signal problem).
  // Listing pages (itemList provided) identify as CollectionPage.
  const url = pageUrl(page.slug || '');
  graph.push({
    '@type': options.itemList?.items.length ? 'CollectionPage' : 'WebPage',
    '@id': url,
    url,
    name: page.title || '',
    isPartOf: { '@id': `${SITE_URL}/#website` },
    ...(page.seo?.metaDescription ? { description: page.seo.metaDescription } : {}),
  });

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
}
